/* Stateless catalogue helpers shared by the selector and its tests. */
(function (root) {
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const sizeKey = (value) => {
    const key = normalize(value).replace(/[\s_-]+/g, '');
    return ({ xsmall: 'xs', extrasmall: 'xs', small: 's', medium: 'm', large: 'l', xlarge: 'xl', extralarge: 'xl', xxlarge: 'xxl' })[key] || key;
  };
  const sizeOrder = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'];

  function prepare(packages, names) {
    return packages.map((pack) => {
      const options = pack.options.map(normalize);
      const colourIndex = options.indexOf(normalize(names.colour));
      const fitIndex = options.indexOf(normalize(names.fit));
      const sizeIndex = options.indexOf(normalize(names.size));
      const valid = fitIndex >= 0 && sizeIndex >= 0 && (colourIndex >= 0 || !!pack.fixedColour);
      const variants = pack.variants.map((variant) => ({
        ...variant,
        colour: normalize(colourIndex >= 0 ? variant.options[colourIndex] : pack.fixedColour),
        fit: normalize(variant.options[fitIndex]),
        size: sizeKey(variant.options[sizeIndex]),
        fitLabel: variant.options[fitIndex] || '',
        sizeLabel: variant.options[sizeIndex] || '',
      }));
      return { ...pack, valid, variants };
    });
  }

  function resolve(pack, selection) {
    if (!pack?.valid) return null;
    const matches = pack.variants.filter((variant) =>
      variant.colour === normalize(selection.colour) &&
      variant.fit === normalize(selection.fit) && variant.size === sizeKey(selection.size)
    );
    // An ambiguous configuration must never choose an arbitrary SKU.
    return matches.length === 1 ? matches[0] : null;
  }

  // Presentation before a size is chosen; this variant must never populate the purchase form.
  function preview(pack, selection) {
    if (!pack?.valid) return { variant: null, priceVaries: false };
    const matches = pack.variants.filter((variant) => variant.colour === normalize(selection.colour));
    const available = matches.filter((variant) => variant.available);
    const candidates = (available.length ? available : matches).slice().sort((a, b) => a.price - b.price);
    return { variant: candidates[0] || null, priceVaries: new Set(candidates.map((variant) => variant.price)).size > 1 };
  }

  function choices(packages) {
    const colours = new Set();
    const fits = new Map();
    packages.filter((pack) => pack.valid).forEach((pack) => pack.variants.forEach((variant) => {
      colours.add(variant.colour);
      if (!fits.has(variant.fit)) fits.set(variant.fit, { key: variant.fit, label: variant.fitLabel, sizes: new Map() });
      fits.get(variant.fit).sizes.set(variant.size, { key: variant.size, label: variant.sizeLabel });
    }));
    return {
      colours: [...colours],
      fits: [...fits.values()].sort((a, b) => {
        const order = { youth: 0, adult: 1 };
        return (order[a.key] ?? 2) - (order[b.key] ?? 2);
      }).map((fit) => ({ ...fit, sizes: [...fit.sizes.values()].sort((a, b) => {
        const rank = (size) => sizeOrder.includes(size.key) ? sizeOrder.indexOf(size.key) : sizeOrder.length;
        return rank(a) - rank(b) || a.label.localeCompare(b.label);
      }) })),
    };
  }

  function initial(packages, defaultPackage, params, initialVariant) {
    const requested = params.get('variant');
    // Language-neutral keys; continue accepting earlier descriptive links.
    const packageKey = normalize(params.get('p') ?? params.get('pack') ?? params.get('package'));
    const requestedColour = normalize(params.get('c') ?? params.get('colour'));
    const validPackages = packages.filter((item) => item.valid);
    const knownColour = validPackages.some((pack) => pack.variants.some((variant) => variant.colour === requestedColour));
    if (requested) {
      for (const pack of validPackages.filter((item) => !packageKey || item.key === packageKey)) {
        const variant = pack.variants.find((item) => String(item.id) === requested && (!requestedColour || item.colour === requestedColour));
        if (variant) return { package: pack.key, ...pick(variant) };
      }
    }
    const pack = validPackages.find((item) => item.key === packageKey) || validPackages.find((item) => item.key === defaultPackage) || validPackages[0];
    // A rejected variant link must not influence the fallback through Liquid's initial variant.
    const variant = (!requested && pack?.variants.find((item) => String(item.id) === String(initialVariant))) || pack?.variants.find((item) => item.available) || pack?.variants[0];
    // Preserve known but unsupported colours (e.g. White Pro) as unavailable, never substitute a SKU.
    return { package: pack?.key, colour: knownColour ? requestedColour : variant?.colour || '', fit: '', size: '' };
  }

  function selectionUrl(href, selection, variant) {
    const url = new URL(href);
    ['pack', 'package', 'colour'].forEach((key) => url.searchParams.delete(key));
    for (const [key, value] of [['p', selection.package], ['c', selection.colour], ['variant', variant?.id]]) {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }
    return url;
  }

  function pick(variant) {
    return { colour: variant?.colour || '', fit: variant?.fit || '', size: variant?.size || '' };
  }

  const api = { normalize, sizeKey, prepare, resolve, preview, choices, initial, selectionUrl };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.AcademyVariants = api;
})(typeof window === 'undefined' ? globalThis : window);
